import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { crmApi, dataQualityApi, workApi } from "@/api/endpoints";
import type { ProducerPriorityItem, UnlinkedClaimItem, WorkItem } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { formatDateTime, statusClass } from "@/utils/format";

function actionClass(action: string): string {
  const key = action.toLowerCase().replace(/\s+/g, "-");
  return `producer-action-${key}`;
}

function PriorityCard({
  item,
  rank,
  onAssist,
  onCreateTask,
  assisting,
}: {
  item: ProducerPriorityItem;
  rank: number;
  onAssist: (item: ProducerPriorityItem, type: "email" | "call_script") => void;
  onCreateTask: (item: ProducerPriorityItem) => void;
  assisting: string | null;
}) {
  const assistKey = (type: string) => `${item.customer_id}-${type}`;

  return (
    <article className="producer-priority-card card">
      <div className="producer-priority-head">
        <span className="producer-priority-rank">#{rank}</span>
        <div className="producer-priority-title">
          <Link to={`/clients/${item.customer_id}`}>
            <strong>{item.display_name}</strong>
          </Link>
          <span className="producer-priority-meta">{item.policy_summary}</span>
        </div>
        <div className="producer-priority-score" title="Priority score">
          {item.priority_score}
        </div>
      </div>

      <div className="producer-reason-tags">
        {item.reason_codes.map((reason) => (
          <span key={reason} className="producer-reason-tag">
            {reason}
          </span>
        ))}
      </div>

      <div className="producer-priority-footer">
        <span className={`producer-suggested-action ${actionClass(item.suggested_action)}`}>
          Suggested: {item.suggested_action}
        </span>
        <div className="producer-action-row">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={assisting === assistKey("email")}
            onClick={() => onAssist(item, "email")}
          >
            {assisting === assistKey("email") ? "Drafting…" : "Draft email"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={assisting === assistKey("call_script")}
            onClick={() => onAssist(item, "call_script")}
          >
            {assisting === assistKey("call_script") ? "Generating…" : "Call script"}
          </button>
          <Link to={`/rate-shop?customerId=${item.customer_id}`} className="btn btn-secondary">
            Rate shop
          </Link>
          <button type="button" className="btn btn-secondary" onClick={() => onCreateTask(item)}>
            Create task
          </button>
        </div>
      </div>
    </article>
  );
}

export function MyWorkPage() {
  const [priorities, setPriorities] = useState<ProducerPriorityItem[]>([]);
  const [items, setItems] = useState<WorkItem[]>([]);
  const [stats, setStats] = useState({ client_tasks: 0, claim_tasks: 0, unlinked_claim_tasks: 0 });
  const [unlinkedClaims, setUnlinkedClaims] = useState<UnlinkedClaimItem[]>([]);
  const [unlinkedTotal, setUnlinkedTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [assisting, setAssisting] = useState<string | null>(null);
  const [assistModal, setAssistModal] = useState<{
    title: string;
    content: string;
    aiEnabled: boolean;
    hint: string | null;
  } | null>(null);

  function reloadInbox() {
    return workApi
      .inbox()
      .then((data) => {
        setItems(data.items);
        setStats({
          client_tasks: data.client_tasks,
          claim_tasks: data.claim_tasks,
          unlinked_claim_tasks: data.unlinked_claim_tasks,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load inbox"));
  }

  useEffect(() => {
    setError(null);
    Promise.all([workApi.producerPriorities(), workApi.inbox(), dataQualityApi.unlinkedClaims()])
      .then(([priorityData, inboxData, unlinkedData]) => {
        setPriorities(priorityData.items);
        setItems(inboxData.items);
        setStats({
          client_tasks: inboxData.client_tasks,
          claim_tasks: inboxData.claim_tasks,
          unlinked_claim_tasks: inboxData.unlinked_claim_tasks,
        });
        setUnlinkedClaims(unlinkedData.items);
        setUnlinkedTotal(unlinkedData.total);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load work queue"));
  }, []);

  async function completeItem(item: WorkItem) {
    const key = `${item.scope}-${item.id}`;
    setCompletingId(key);
    setError(null);
    const previous = items;
    setItems((current) => current.filter((row) => `${row.scope}-${row.id}` !== key));
    try {
      await workApi.completeItem(item.scope, item.id);
      await reloadInbox();
    } catch (err) {
      setItems(previous);
      setError(err instanceof Error ? err.message : "Failed to complete task");
    } finally {
      setCompletingId(null);
    }
  }

  async function handleAssist(item: ProducerPriorityItem, assistType: "email" | "call_script") {
    const key = `${item.customer_id}-${assistType}`;
    setAssisting(key);
    setError(null);
    try {
      const response = await workApi.producerAssist(item.customer_id, {
        assist_type: assistType,
        reason_codes: item.reason_codes,
      });
      setAssistModal({
        title: assistType === "email" ? `Email draft — ${item.display_name}` : `Call script — ${item.display_name}`,
        content: response.content,
        aiEnabled: response.ai_enabled,
        hint: response.prompt_hint,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate outreach copy");
    } finally {
      setAssisting(null);
    }
  }

  async function handleCreateTask(item: ProducerPriorityItem) {
    const title = item.reason_codes[0] ?? `Follow up with ${item.display_name}`;
    setError(null);
    try {
      await crmApi.createTask(item.customer_id, {
        title: title.slice(0, 120),
        description: item.reason_codes.join("\n"),
      });
      await reloadInbox();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create task");
    }
  }

  return (
    <>
      <PageHeader
        title="My Work"
        subtitle="Your daily producer driver — ranked accounts to touch, plus your task inbox."
      />
      {error ? <div className="error-banner">{error}</div> : null}

      {unlinkedTotal > 0 ? (
        <div className="card" style={{ padding: "1rem", marginBottom: "1rem", borderColor: "var(--warning)" }}>
          <strong>{unlinkedTotal} claim(s)</strong> are not linked to a client profile. Link them to keep CRM
          and claims on one record. The <strong>Unlinked claim tasks</strong> metric below counts tasks missing
          a customer link.
          <ul style={{ margin: "0.75rem 0 0", paddingLeft: "1.25rem" }}>
            {unlinkedClaims.map((row) => (
              <li key={row.id} style={{ marginBottom: "0.35rem" }}>
                <Link to={`/claims/${row.id}`}>{row.external_ref ?? row.id.slice(0, 8)}</Link>
                {row.policy_number ? ` · ${row.policy_number}` : ""}
                {row.claimant_email ? ` · ${row.claimant_email}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <section className="stat-grid">
        <StatCard label="Accounts to touch" value={priorities.length} />
        <StatCard label="Client tasks" value={stats.client_tasks} />
        <StatCard label="Claim tasks" value={stats.claim_tasks} />
        <StatCard label="Unlinked claim tasks" value={stats.unlinked_claim_tasks} />
      </section>

      <section className="producer-priorities-section">
        <header className="section-header">
          <h2>Accounts to call today</h2>
          <p>Ranked by renewal urgency, book value, contact gaps, open claims, and cross-sell signals.</p>
        </header>
        {priorities.length === 0 ? (
          <div className="card empty-state">Your book looks healthy — no urgent outreach flagged.</div>
        ) : (
          <div className="producer-priority-grid">
            {priorities.map((item, index) => (
              <PriorityCard
                key={item.customer_id}
                item={item}
                rank={index + 1}
                onAssist={handleAssist}
                onCreateTask={handleCreateTask}
                assisting={assisting}
              />
            ))}
          </div>
        )}
      </section>

      <section style={{ marginTop: "1.5rem" }}>
        <header className="section-header">
          <h2>Task inbox</h2>
          <p>Open client and claim tasks assigned to you.</p>
        </header>
        <div className="card">
          {items.length === 0 ? (
            <div className="empty-state">Inbox is clear.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Task</th>
                  <th>Scope</th>
                  <th>Client / Claim</th>
                  <th>Status</th>
                  <th>Due</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const rowKey = `${item.scope}-${item.id}`;
                  return (
                    <tr key={rowKey}>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={completingId === rowKey}
                          onClick={() => completeItem(item)}
                          title="Mark complete"
                        >
                          {completingId === rowKey ? "…" : "Done"}
                        </button>
                      </td>
                      <td>{item.title}</td>
                      <td>{item.scope}</td>
                      <td>
                        {item.customer_id ? (
                          <Link to={`/clients/${item.customer_id}`}>{item.customer_name}</Link>
                        ) : null}
                        {item.claim_id ? (
                          <>
                            {item.customer_id ? " · " : null}
                            <Link to={`/claims/${item.claim_id}`}>{item.claim_ref ?? "Claim"}</Link>
                          </>
                        ) : null}
                      </td>
                      <td>
                        <span className={statusClass(item.status)}>{item.status}</span>
                      </td>
                      <td>{formatDateTime(item.due_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {assistModal ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setAssistModal(null)}>
          <div className="modal card producer-assist-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h3>{assistModal.title}</h3>
              <button type="button" className="btn btn-secondary" onClick={() => setAssistModal(null)}>
                Close
              </button>
            </header>
            {!assistModal.aiEnabled && assistModal.hint ? (
              <p className="producer-assist-hint">{assistModal.hint}</p>
            ) : null}
            <pre className="producer-assist-content">{assistModal.content}</pre>
          </div>
        </div>
      ) : null}
    </>
  );
}
